/**
 * 支払管理（外注費）— 元請モデル。
 * 顧客決済済み(released)の案件について、業者への手取り(vendor_payout)を「支良洲工業の銀行振込」で支払う運用。
 * ここでは未振込/振込済みを管理し、「振込済みにする」で消込(payout_at)を記録する。
 * ※ 送金自体はプラットフォーム外（当社の銀行振込）。他者資金の預託・分配は行わない（資金移動業に非該当）。
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Landmark, Check, Copy, RotateCcw, AlertTriangle } from "lucide-react";
import { adminApi, type PayoutRow } from "@/lib/api";

const caseNo = (n?: number | null) => (n ? `No.${String(n).padStart(4, "0")}` : "—");

export function PayoutsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["payouts"], queryFn: () => adminApi.listPayouts() });
  const [tab, setTab] = useState<"unpaid" | "paid">("unpaid");

  const rows = data ?? [];
  const unpaid = rows.filter((r) => !r.payout_at);
  const paid = rows.filter((r) => !!r.payout_at);
  const list = tab === "unpaid" ? unpaid : paid;
  const unpaidTotal = useMemo(() => unpaid.reduce((s, r) => s + r.vendor_payout, 0), [unpaid]);

  const markMut = useMutation({
    mutationFn: (id: string) => adminApi.markPayout(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payouts"] }),
  });
  const unmarkMut = useMutation({
    mutationFn: (id: string) => adminApi.unmarkPayout(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payouts"] }),
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <h2 className="text-lg font-semibold">支払管理（外注費の振込）</h2>
      <p className="text-xs text-ink-soft dark:text-white/60">
        顧客の決済が完了した案件の<strong>業者手取り</strong>を、シタミー運営（合同会社支良洲工業）から各業者の口座へ振込みます。振込後に「振込済み」で消込してください。
      </p>

      <div className="rounded-xl bg-brand-50 dark:bg-brand-800/30 border border-brand-200 dark:border-brand-700 p-3 flex items-center justify-between">
        <span className="text-sm font-bold text-brand-900 dark:text-brand-100">未振込 合計</span>
        <span className="text-xl font-black text-brand-700 dark:text-brand-200 tabular-nums">¥{unpaidTotal.toLocaleString("ja-JP")}</span>
      </div>

      <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-sm">
        <button onClick={() => setTab("unpaid")} className={`px-3 py-1.5 ${tab === "unpaid" ? "bg-brand-500 text-white" : ""}`}>未振込 {unpaid.length}</button>
        <button onClick={() => setTab("paid")} className={`px-3 py-1.5 ${tab === "paid" ? "bg-brand-500 text-white" : ""}`}>振込済み {paid.length}</button>
      </div>

      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {!isLoading && list.length === 0 && <p className="text-sm text-ink-soft py-6 text-center">該当はありません。</p>}

      <ul className="space-y-2">
        {list.map((r) => (
          <li key={r.id} className="rounded-xl border border-[var(--color-border)] p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-black tabular-nums px-2 py-0.5 rounded-md bg-brand-500 text-white">{caseNo(r.case_no)}</span>
                  <span className="font-medium truncate">{r.vendor_company || "（業者名未登録）"}</span>
                </div>
                <BankLine r={r} />
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] text-ink-soft dark:text-white/50">振込額（手取り）</div>
                <div className="text-lg font-black tabular-nums">¥{r.vendor_payout.toLocaleString("ja-JP")}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {r.payout_at ? (
                <>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 dark:text-brand-300"><Check size={13} /> 振込済み（{new Date(r.payout_at).toLocaleDateString("ja-JP")}）</span>
                  <button onClick={() => unmarkMut.mutate(r.id)} disabled={unmarkMut.isPending} className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[var(--color-border)]">
                    <RotateCcw size={12} /> 未振込に戻す
                  </button>
                </>
              ) : (
                <button
                  onClick={() => markMut.mutate(r.id)}
                  disabled={markMut.isPending || !hasBank(r)}
                  className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-brand-500 text-white font-bold disabled:opacity-50">
                  <Landmark size={13} /> 振込済みにする
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function hasBank(r: PayoutRow) {
  return !!(r.bank && r.branch && r.account && r.account_holder);
}

function BankLine({ r }: { r: PayoutRow }) {
  if (!hasBank(r)) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1">
        <AlertTriangle size={12} /> 業者の口座が未登録（業者アプリの売上→振込先口座で登録が必要）
      </p>
    );
  }
  const line = `${r.bank} ${r.branch} ${r.account_type ?? "普通"} ${r.account} ${r.account_holder}`;
  return (
    <p className="text-xs text-ink-soft dark:text-white/60 mt-1 flex items-center gap-1">
      <Landmark size={11} /> {line}
      <button onClick={() => navigator.clipboard?.writeText(line)} className="ml-1 text-ink-soft hover:text-brand-600" title="口座情報をコピー"><Copy size={11} /></button>
    </p>
  );
}
